/* eslint-disable react/no-unknown-property */
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@pypestream/design-system';

// The button and dialog styles, for callers outside the node panel (Build
// with AI in the Ask AI dock).
import './node-property-panel.scss';

/**
 * The expand button and the large editor it opens, for any scrolling text box
 * in the right-hand panel.
 *
 * The panel is narrow and its long-text boxes (a Gemini system prompt in a
 * Parameter Input, Raw JSON, a Build with AI brief) are capped in height and
 * scroll, so reading or editing a real prompt meant scrolling a small box
 * inside a scrolling panel. The button opens the SAME value in a dialog sized
 * to the window.
 *
 * The dialog edits through the caller's own handlers, so nothing about how a
 * field commits changes: `onOpen` fires when it opens and `onClose` when it
 * closes — callers pass the same focus/blur pair the inline box fires — so a
 * field's undo snapshot and save happen exactly once per editing session.
 */
export const TextExpander: FC<{
  /** For the dialog's title and the button's accessible name. */
  labelText: string;
  value: string;
  disabled?: boolean;
  testId: string;
  /** Extra class for the button, e.g. to place it over the box. */
  buttonClassName?: string;
  onOpen?: () => void;
  onChange: (value: string) => void;
  onClose?: () => void;
}> = ({
  labelText,
  value,
  disabled,
  testId,
  buttonClassName,
  onOpen,
  onChange,
  onClose,
}) => {
  const [expanded, setExpanded] = useState(false);
  const editorRef = useRef<HTMLTextAreaElement>(null);

  const close = () => {
    setExpanded(false);
    onClose?.();
  };

  useEffect(() => {
    if (!expanded) return undefined;
    editorRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // The panel and the table both listen for Escape; this one is
        // answered here and goes no further.
        e.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // `close` only reads props that are stable for an open dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  return (
    <>
      <button
        type="button"
        className={`c-node-property__expand${
          buttonClassName ? ` ${buttonClassName}` : ''
        }`}
        test-id={`${testId}-expand`}
        aria-label={`Expand ${labelText}`}
        title="Expand"
        onClick={() => {
          onOpen?.();
          setExpanded(true);
        }}
      >
        <Icon name="expand" size="xsmall" />
      </button>
      {expanded &&
        createPortal(
          <div
            className="c-expanded-text"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) close();
            }}
          >
            <div
              className="c-expanded-text__dialog"
              role="dialog"
              aria-modal="true"
              aria-label={labelText}
              test-id={`${testId}-expanded`}
            >
              <div className="c-expanded-text__head">
                <span className="c-expanded-text__title">{labelText}</span>
                <button
                  type="button"
                  className="c-expanded-text__done"
                  test-id={`${testId}-collapse`}
                  onClick={close}
                >
                  Done
                </button>
              </div>
              <textarea
                ref={editorRef}
                className="c-expanded-text__editor"
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.currentTarget.value)}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

/**
 * A node-panel textarea with the expand button. With a `label`, the button
 * sits at the end of the label row; without one (a box under a "Back" link),
 * it sits over the box's top-right corner.
 */
export const ExpandableTextarea: FC<{
  label?: ReactNode;
  /** For the dialog's title and the button's accessible name. */
  labelText: string;
  value: string;
  disabled?: boolean;
  className: string;
  testId: string;
  onFocus?: () => void;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** Rendered under the inline textarea, e.g. a help line. */
  children?: ReactNode;
}> = ({
  label,
  labelText,
  value,
  disabled,
  className,
  testId,
  onFocus,
  onChange,
  onBlur,
  children,
}) => {
  const expander = (buttonClassName?: string) => (
    <TextExpander
      labelText={labelText}
      value={value}
      disabled={disabled}
      testId={testId}
      buttonClassName={buttonClassName}
      onOpen={onFocus}
      onChange={onChange}
      onClose={onBlur}
    />
  );
  const textarea = (
    <textarea
      className={className}
      test-id={testId}
      value={value}
      disabled={disabled}
      onFocus={onFocus}
      onChange={(e) => onChange(e.currentTarget.value)}
      onBlur={onBlur}
    />
  );

  if (!label) {
    return (
      <>
        <div className="c-node-property__expandable">
          {textarea}
          {expander('c-node-property__expand--corner')}
        </div>
        {children}
      </>
    );
  }

  return (
    <>
      <div className="c-node-property__label-row">
        {label}
        {expander()}
      </div>
      {textarea}
      {children}
    </>
  );
};
